import React, { useEffect, useState } from 'react';
import {
    Button,
    Container,
    Grid,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Paper,
    Theme,
    Toolbar,
    Typography
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import HealthServiceInstance, { IPoisonQueue } from '../../../services/HealthService';
import OntimeBreadcrumb from '../../common/OntimeBreadcrumb';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        content: {
            flexGrow: 1,
            height: '100vh',
            overflow: 'auto',
        },
        container: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(4),
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
    })
);

export default function PoisonQueues() {
    const classes = useStyles();
    const { t } = useTranslation('common');
    const { enqueueSnackbar } = useSnackbar();
    const [queues, setQueues] = useState<IPoisonQueue[]>([]);
    const [processingQueues, setProcessingQueues] = useState<Set<string>>(new Set());

    const fetchQueues = React.useCallback((cancelToken?: import('axios').CancelToken) => {
        return HealthServiceInstance.getPoisonQueues(cancelToken).then(setQueues);
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();
        fetchQueues(source.token).catch(reason => {
            if (!axios.isCancel(reason)) {
                console.error(reason);
                enqueueSnackbar(t('poisonQueues.loadError'), { variant: 'error' });
            }
        });
        return () => {
            source.cancel();
        };
    }, [fetchQueues, enqueueSnackbar, t]);

    const handleReprocess = async (queueName: string) => {
        setProcessingQueues(prev => new Set(prev).add(queueName));
        try {
            await HealthServiceInstance.reprocessPoisonQueue(queueName);
            enqueueSnackbar(t('poisonQueues.reprocessSuccess'), { variant: 'success' });
            await fetchQueues();
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('poisonQueues.reprocessError'), { variant: 'error' });
        } finally {
            setProcessingQueues(prev => {
                const next = new Set(prev);
                next.delete(queueName);
                return next;
            });
        }
    };

    return (
        <div className={classes.content}>
            <Container maxWidth="lg" className={classes.container}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={{}} current={t('poisonQueues.title')} />
                    </Grid>
                    <Grid item xs={12}>
                        <Paper className={classes.paper}>
                            <Toolbar>
                                <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                    {t('poisonQueues.title')}
                                </Typography>
                            </Toolbar>
                            <List>
                                {queues.length === 0 && (
                                    <ListItem>
                                        <ListItemText primary={t('poisonQueues.noQueues')} />
                                    </ListItem>
                                )}
                                {queues.map((queue) => (
                                    <ListItem key={queue.queueName}>
                                        <ListItemText
                                            primary={queue.queueName}
                                            secondary={t('poisonQueues.messageCount', { count: queue.messageCount })}
                                        />
                                        <ListItemSecondaryAction>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={processingQueues.has(queue.queueName)}
                                                onClick={() => handleReprocess(queue.queueName)}
                                            >
                                                {t('poisonQueues.reprocess')}
                                            </Button>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
}
